import { useCallback, useEffect, useRef } from "react";

/**
 * Tracks the timestamp of the most recent scroll / wheel / touch-scroll and
 * returns a guard `() => boolean` that is true when one happened within `ms`.
 *
 * Use it to suppress hover-to-switch interactions: when the page scrolls under a
 * stationary cursor, the browser fires `mouseenter` on whatever element slides
 * beneath the pointer — which would otherwise trigger a switch the user never
 * intended (e.g. a segmented control collapsing to a one-item category mid-
 * scroll). Genuine pointer movement, with no recent scroll, is unaffected.
 */
export function useRecentScroll(ms = 250): () => boolean {
  const last = useRef(0);

  useEffect(() => {
    const mark = () => {
      last.current = performance.now();
    };
    // Capture so scrolls inside nested scroll containers count too; wheel/touch
    // fire slightly ahead of the `scroll` event, closing the ordering gap before
    // the scroll-induced `mouseenter` arrives.
    const opts = { capture: true, passive: true } as const;
    window.addEventListener("scroll", mark, opts);
    window.addEventListener("wheel", mark, opts);
    window.addEventListener("touchmove", mark, opts);
    return () => {
      window.removeEventListener("scroll", mark, opts);
      window.removeEventListener("wheel", mark, opts);
      window.removeEventListener("touchmove", mark, opts);
    };
  }, []);

  return useCallback(() => performance.now() - last.current < ms, [ms]);
}
